/**
 * Dice Store API — fetch the weekly rotation + spend gold/tokens to unlock a die.
 *
 * The server is the source of truth for both the rotation and the spend; the
 * client only displays prices and renders what the server delivered (server
 * emits BALANCE_UPDATE + DICE_GEAR_UPDATED on success, so the wallet and the
 * owned-dice grid refresh through the existing listeners).
 */

import { serverConfig } from '../network/ServerConfig';
import type { DiceStoreCurrency, DiceStoreListing, DiceStorePurchaseResult } from '@sdl/shared';

/** Fetch the current weekly rotation. Returns null on any failure. */
export async function fetchDiceStore(): Promise<DiceStoreListing | null> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/dice-store`);
    if (!resp.ok) return null;
    return (await resp.json()) as DiceStoreListing;
  } catch {
    return null;
  }
}

/** Spend gold or a token to unlock a die. Never throws — returns `{ ok: false, error }`. */
export async function purchaseDice(
  diceId: string,
  currency: DiceStoreCurrency,
  token: string,
): Promise<DiceStorePurchaseResult> {
  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/dice-store/purchase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ diceId, currency }),
    });
    const body = (await resp.json().catch(() => ({}))) as DiceStorePurchaseResult;
    if (!resp.ok || !body?.ok) {
      return { ok: false, error: body?.error ?? `purchase_failed_${resp.status}` };
    }
    return body;
  } catch {
    return { ok: false, error: 'network_error' };
  }
}
